import { mkShortId } from "@/wab/shared/common";
import {
  MediaAsset,
  ProjectDependency,
  Site,
} from "@/wab/shared/model/classes";
import { fromBuffer } from "file-type/browser";
import { isEqual } from "lodash";
import { z } from "zod";
export const MAX_MEDIA_ASSET_BYTES = 20 * 1024 * 1024;
export const MAX_MEDIA_PACKAGE_BYTES = 64 * 1024 * 1024;
export const MAX_MEDIA_ASSETS = 256;
const mimeKind = {
  "audio/wav": "audio", "audio/mpeg": "audio", "audio/ogg": "audio",
  "audio/mp4": "audio", "video/mp4": "video", "video/webm": "video", "audio/webm": "audio", "video/ogg": "video",
} as const;
export const mediaImportInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  kind: z.enum(["audio", "video"]),
  dataUri: z.string(),
  expectedSha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).strict();
export type PreparedMediaAsset = Readonly<{
  name: string; kind: "audio" | "video"; mimeType: string;
  dataUri: string; byteLength: number; sha256: string;
}>;
const prepared = new WeakSet<object>();
/** Canonical inline bytes only. File detection establishes container type, not
 * codec correctness or successful playback. No network/storage effects. */
export async function prepareMediaAsset(raw: unknown, signal?: AbortSignal): Promise<PreparedMediaAsset> {
  const stop = () => { if (signal?.aborted) throw new Error("MEDIA_CANCELED"); };
  stop();
  const input = mediaImportInputSchema.parse(raw);
  if (input.dataUri.length > Math.ceil(MAX_MEDIA_ASSET_BYTES / 3) * 4 + 100) throw new Error("MEDIA_TOO_LARGE");
  const match = /^data:([a-z]+\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/]*={0,2})$/.exec(input.dataUri);
  if (!match || !match[2]) throw new Error("MEDIA_ENCODING_INVALID");
  const declared = match[1];
  const kind = mimeKind[declared as keyof typeof mimeKind];
  if (!kind || kind !== input.kind) throw new Error("MEDIA_TYPE_MISMATCH");
  if (match[2].length % 4 || match[2].length / 4 * 3 - (match[2].endsWith("==") ? 2 : match[2].endsWith("=") ? 1 : 0) > MAX_MEDIA_ASSET_BYTES) throw new Error("MEDIA_TOO_LARGE");
  const binary = atob(match[2]);
  if (!binary.length || binary.length > MAX_MEDIA_ASSET_BYTES) throw new Error("MEDIA_TOO_LARGE");
  if (btoa(binary) !== match[2]) throw new Error("MEDIA_ENCODING_INVALID");
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const detected = await fromBuffer(bytes);
  stop();
  const actual = detected?.mime === "audio/vnd.wave" ? "audio/wav" : detected?.mime;
  if (actual !== declared) throw new Error("MEDIA_TYPE_MISMATCH");
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  stop();
  const sha256 = Array.from(hash, (b) => b.toString(16).padStart(2,"0")).join("");
  if (input.expectedSha256 && input.expectedSha256 !== sha256) throw new Error("MEDIA_INTEGRITY_MISMATCH");
  const result = Object.freeze({ name: input.name, kind: input.kind, mimeType: declared,
    dataUri: input.dataUri, byteLength: bytes.byteLength, sha256 });
  prepared.add(result);
  return result;
}
/** Must execute within the existing native document recorder/authority guard.
 * Only this module's immutable verified preparation receipts can be committed. */
export function commitMediaAsset(site: Site, value: PreparedMediaAsset, expectedLocalAssetIds: string[]): MediaAsset {
  if (!prepared.has(value)) throw new Error("MEDIA_UNVERIFIED_PREPARATION");
  if (!isEqual(site.mediaAssets.map((a) => a.uuid), expectedLocalAssetIds)) throw new Error("MEDIA_INVENTORY_CHANGED");
  const existing = site.mediaAssets.find((a) => a.kind === value.kind && a.mimeType === value.mimeType && a.sha256 === value.sha256 && a.byteLength === value.byteLength && a.dataUri === value.dataUri);
  if (existing) return existing;
  if (site.mediaAssets.length >= MAX_MEDIA_ASSETS || site.mediaAssets.reduce((n,a) => n+a.byteLength,0)+value.byteLength > MAX_MEDIA_PACKAGE_BYTES) throw new Error("MEDIA_PACKAGE_TOO_LARGE");
  const asset = new MediaAsset({ uuid: mkShortId(), ...value });
  site.mediaAssets.push(asset);
  return asset;
}
export function cloneMediaAsset(asset: MediaAsset): MediaAsset {
  return new MediaAsset({ uuid: mkShortId(), name: asset.name, kind: asset.kind,
    mimeType: asset.mimeType, dataUri: asset.dataUri, byteLength: asset.byteLength, sha256: asset.sha256 });
}

/** Structural shape shared by a live MediaAsset and its serialized persisted
 * bundle record, so the same integrity pipeline runs on both. */
export type PersistedMediaAssetRecord = Pick<
  MediaAsset,
  "name" | "kind" | "mimeType" | "dataUri" | "byteLength" | "sha256"
>;

/**
 * Validates a persisted MediaAsset on load: verifies canonical base64 formatting,
 * MIME type consistency, detected container signature, byte size bounds, and SHA-256 integrity.
 */
export async function validatePersistedMediaAsset(
  asset: PersistedMediaAssetRecord,
  signal?: AbortSignal
): Promise<PreparedMediaAsset> {
  const stop = () => { if (signal?.aborted) throw new Error("MEDIA_CANCELED"); };
  stop();
  if (asset.byteLength <= 0 || asset.byteLength > MAX_MEDIA_ASSET_BYTES) {
    throw new Error("MEDIA_TOO_LARGE");
  }
  if (asset.dataUri.length > Math.ceil(MAX_MEDIA_ASSET_BYTES / 3) * 4 + 100) {
    throw new Error("MEDIA_TOO_LARGE");
  }
  const match = /^data:([a-z]+\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/]*={0,2})$/.exec(asset.dataUri);
  if (!match || !match[2]) throw new Error("MEDIA_ENCODING_INVALID");
  const declared = match[1];
  if (declared !== asset.mimeType) throw new Error("MEDIA_TYPE_MISMATCH");
  const kind = mimeKind[declared as keyof typeof mimeKind];
  if (!kind || kind !== asset.kind) throw new Error("MEDIA_TYPE_MISMATCH");
  if (
    match[2].length % 4 ||
    match[2].length / 4 * 3 - (match[2].endsWith("==") ? 2 : match[2].endsWith("=") ? 1 : 0) > MAX_MEDIA_ASSET_BYTES
  ) {
    throw new Error("MEDIA_TOO_LARGE");
  }
  const binary = atob(match[2]);
  if (!binary.length || binary.length > MAX_MEDIA_ASSET_BYTES || binary.length !== asset.byteLength) {
    throw new Error("MEDIA_TOO_LARGE");
  }
  if (btoa(binary) !== match[2]) throw new Error("MEDIA_ENCODING_INVALID");
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const detected = await fromBuffer(bytes);
  stop();
  const actual = detected?.mime === "audio/vnd.wave" ? "audio/wav" : detected?.mime;
  if (actual !== declared) throw new Error("MEDIA_TYPE_MISMATCH");
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  stop();
  const sha256 = Array.from(hash, (b) => b.toString(16).padStart(2, "0")).join("");
  if (asset.sha256 !== sha256) throw new Error("MEDIA_INTEGRITY_MISMATCH");
  const result = Object.freeze({
    name: asset.name,
    kind: asset.kind,
    mimeType: declared,
    dataUri: asset.dataUri,
    byteLength: bytes.byteLength,
    sha256,
  });
  prepared.add(result);
  return result;
}

/**
 * Validates all persisted media assets in a Site upon load, enforcing per-asset
 * and aggregate bounds (256 local assets, 64MiB aggregate).
 */
export async function validatePersistedMediaAssets(
  site: Site,
  signal?: AbortSignal
): Promise<void> {
  const stop = () => { if (signal?.aborted) throw new Error("MEDIA_CANCELED"); };
  stop();
  if (site.mediaAssets.length > MAX_MEDIA_ASSETS) {
    throw new Error("MEDIA_PACKAGE_TOO_LARGE");
  }
  let totalBytes = 0;
  for (const asset of site.mediaAssets) {
    stop();
    await validatePersistedMediaAsset(asset, signal);
    totalBytes += asset.byteLength;
    if (totalBytes > MAX_MEDIA_PACKAGE_BYTES) {
      throw new Error("MEDIA_PACKAGE_TOO_LARGE");
    }
  }
}

/**
 * Persisted-load integrity boundary: resolves the Site owned by an unbundled
 * root instance (a raw Site, or a ProjectDependency wrapping one) and enforces
 * persisted media integrity before it can render or codegen. Fail-closed:
 * throws on tampered/invalid bytes. Instances with no owned Site (and legacy
 * sites with an empty/absent mediaAssets list) pass through untouched.
 */
export async function validatePersistedMediaInInst(
  inst: unknown,
  signal?: AbortSignal
): Promise<void> {
  const site = Site.isKnown(inst)
    ? inst
    : ProjectDependency.isKnown(inst)
    ? inst.site
    : undefined;
  if (site?.mediaAssets?.length) {
    await validatePersistedMediaAssets(site, signal);
  }
}

/** Raw serialized MediaAsset records carried by a persisted bundle map. */
export function persistedMediaRecords(bundle: {
  map?: Record<string, any>;
}): (PersistedMediaAssetRecord & { uuid?: string })[] {
  return Object.values(bundle.map ?? {}).filter(
    (r): r is PersistedMediaAssetRecord & { uuid?: string } =>
      r?.__type === "MediaAsset"
  );
}


/** Existing asset in the live site, with its bundle iid when known. */
export type InventoryEntry = {
  uuid: string;
  byteLength: number;
  iid?: string;
  /** Final member record (live asset or incoming serialized record). */
  record?: any;
};

/**
 * The Site record a bundle actually installs into the inventory owner: the
 * root Site, or the Site referenced by a root ProjectDependency's `site`. A
 * root whose Site record is omitted from a partial yields no replacement; an
 * unrelated (orphan) Site record never substitutes for it. Unsupported roots,
 * non-local or wrong-typed `site` refs, and a second Site record that would
 * overwrite the live Site address are refused before any mutation.
 */
function resolveRootSiteRecord(
  bundle: { root?: string; map?: Record<string, any> },
  map: Record<string, any>,
  siteIid: string | undefined,
  invalid: string
): any | undefined {
  const live = siteIid !== undefined ? map[siteIid] : undefined;
  const liveSite = live?.__type === "Site" ? live : undefined;
  let siteRec: any | undefined;
  // Iid of the Site the bundle would install into, when it is addressable.
  let ownerIid: string | undefined;
  if (bundle.root !== undefined) {
    const rootRec = map[bundle.root];
    if (rootRec === undefined) {
      siteRec = undefined;
    } else if (rootRec.__type === "Site") {
      siteRec = rootRec;
      ownerIid = bundle.root;
    } else if (rootRec.__type === "ProjectDependency") {
      const ref = rootRec.site;
      if (
        !ref ||
        typeof ref !== "object" ||
        "__xref" in ref ||
        typeof ref.__ref !== "string"
      ) {
        throw new Error(invalid);
      }
      const target = map[ref.__ref];
      if (target !== undefined && target?.__type !== "Site") {
        throw new Error(invalid);
      }
      siteRec = target;
      ownerIid = ref.__ref;
    } else {
      throw new Error(invalid);
    }
  } else {
    const sites = Object.entries(map).filter(([, r]) => r?.__type === "Site");
    if (siteIid !== undefined) {
      // A known owner admits no other Site record in a rootless partial.
      if (sites.some(([iid]) => iid !== siteIid)) throw new Error(invalid);
    } else if (sites.length > 1) {
      throw new Error(invalid);
    }
    siteRec = sites[0]?.[1];
    ownerIid = sites[0]?.[0];
  }
  // The known actual owner constrains the explicit root / dependency site even
  // when its own record is absent from the incoming map.
  if (siteRec !== undefined && siteIid !== undefined && ownerIid !== siteIid) {
    throw new Error(invalid);
  }
  if (liveSite && liveSite !== siteRec) throw new Error(invalid);
  return siteRec;
}

/**
 * Final owned-asset inventory after applying a partial bundle the way
 * `Bundler.unbundlePartial` does. A Site record carrying the array field
 * replaces the whole array: every member is a LOCAL `__ref` resolved to an
 * incoming record of the right type or to a retained live asset (not in
 * `deletedIids`); the result keeps native array cardinality and order. Any
 * other member (xref, non-ref, unresolvable, wrong type, deleted), any repeated
 * iid or uuid, and any array longer than `opts.max` throws before mutation.
 * Without the array field, live assets stay (minus `deletedIids`) and an
 * incoming record replaces the live asset with its iid. Records neither
 * referenced nor replacing a live asset are orphans and do not count. Live
 * assets with no known iid are matched by uuid. Entries keep their iid so that
 * several partial bundles can be chained.
 */
export function projectPartialInventory(
  bundle: { root?: string; map?: Record<string, any> },
  field: "mediaAssets" | "fontFaceAssets",
  type: "MediaAsset" | "FontFaceAsset",
  existing: Iterable<InventoryEntry>,
  deletedIids?: Iterable<string>,
  opts?: { invalid: string; tooLarge: string; max: number },
  siteIid?: string
): InventoryEntry[] {
  const invalid = opts?.invalid ?? "INVENTORY_MEMBERSHIP_INVALID";
  const map = bundle.map ?? {};
  const deleted = new Set(deletedIids ?? []);
  const live = [...existing];
  const liveByIid = new Map(
    live.filter((e) => e.iid !== undefined).map((e) => [e.iid!, e])
  );
  const siteRec = resolveRootSiteRecord(bundle, map, siteIid, invalid);
  const incomingByIid = new Map<string, any>();
  const incomingUuids = new Set<string>();
  for (const [iid, rec] of Object.entries(map)) {
    if (rec?.__type === type) {
      incomingByIid.set(iid, rec);
      if (typeof rec.uuid === "string") incomingUuids.add(rec.uuid);
    }
  }
  const members: any[] | undefined =
    siteRec && field in siteRec ? siteRec[field] : undefined;
  if (members !== undefined) {
    if (!Array.isArray(members)) throw new Error(invalid);
    if (opts && members.length > opts.max) throw new Error(opts.tooLarge);
    const iids = new Set<string>();
    const uuids = new Set<string>();
    const result: InventoryEntry[] = [];
    for (const ref of members) {
      const iid =
        ref && typeof ref === "object" && !("__xref" in ref)
          ? ref.__ref
          : undefined;
      if (typeof iid !== "string" || iids.has(iid)) throw new Error(invalid);
      iids.add(iid);
      let entry: InventoryEntry | undefined;
      const rec = incomingByIid.get(iid);
      if (rec) {
        entry = { uuid: rec.uuid, byteLength: rec.byteLength, iid, record: rec };
      } else if (map[iid] === undefined && !deleted.has(iid)) {
        entry = liveByIid.get(iid);
      }
      if (!entry || uuids.has(entry.uuid)) throw new Error(invalid);
      uuids.add(entry.uuid);
      result.push(entry);
    }
    return result;
  }
  const result: InventoryEntry[] = [];
  for (const e of live) {
    if (e.iid !== undefined && deleted.has(e.iid)) continue;
    if (e.iid !== undefined && incomingByIid.has(e.iid)) continue;
    if (e.iid === undefined && incomingUuids.has(e.uuid)) continue;
    result.push(e);
  }
  for (const [iid, rec] of incomingByIid) {
    if (
      liveByIid.has(iid) ||
      live.some((e) => e.iid === undefined && e.uuid === rec.uuid)
    ) {
      result.push({
        uuid: rec.uuid,
        byteLength: rec.byteLength,
        iid,
        record: rec,
      });
    }
  }
  return result;
}

/** Native media identity is a bounded nonempty id (mkShortId nanoid), never an
 * RFC UUID; a persisted record without one has no accountable identity. */
export const MAX_MEDIA_ASSET_ID_LENGTH = 128;

function persistedMediaAssetUuid(rec: { uuid?: unknown }): string {
  const uuid = rec.uuid;
  if (
    typeof uuid !== "string" ||
    uuid.length === 0 ||
    uuid.length > MAX_MEDIA_ASSET_ID_LENGTH
  ) {
    throw new Error("MEDIA_IDENTITY_INVALID");
  }
  return uuid;
}

const MEDIA_IDENTITY_FIELDS = [
  "name",
  "kind",
  "mimeType",
  "dataUri",
  "byteLength",
  "sha256",
] as const;

/**
 * Pre-mutation persisted-media boundary for incremental/partial bundle loads:
 * verifies each serialized MediaAsset record's canonical bytes, detected
 * container, and SHA-256 BEFORE the bundle is unbundled into a live site, and
 * enforces package bounds against the PROJECTED final owned inventory under
 * partial-bundle semantics: an incoming record replaces any existing asset
 * with the same uuid (so its new byteLength supersedes the old one), repeated
 * identical declarations of one uuid count once, and conflicting duplicate
 * identity definitions are rejected. Records duplicated by package
 * dependencies pass without double counting or double hashing.
 */
export async function validatePersistedMediaRecords(
  bundles:
    | Iterable<{ map?: Record<string, any> }>
    | { map?: Record<string, any> },
  opts?: {
    site?: Site;
    signal?: AbortSignal;
    /** iid of a live asset in the site's bundle (for iid-based projection). */
    iidOf?: (asset: MediaAsset) => string | undefined;
    deletedIids?: Iterable<string>;
    depBundles?: Iterable<{ map?: Record<string, any> }>;
  }
): Promise<void> {
  const signal = opts?.signal;
  const stop = () => {
    if (signal?.aborted) throw new Error("MEDIA_CANCELED");
  };

  if (opts?.depBundles) {
    for (const depBundle of opts.depBundles) {
      stop();
      const depProjected = new Map<string, number>();
      const depSeen = new Map<string, PersistedMediaAssetRecord>();
      let depTotal = 0;
      for (const rec of persistedMediaRecords(depBundle)) {
        stop();
        const uuid = persistedMediaAssetUuid(rec);
        const prior = depSeen.get(uuid);
        if (prior) {
          if (
            MEDIA_IDENTITY_FIELDS.some(
              (field) => !isEqual(prior[field], rec[field])
            )
          ) {
            throw new Error("MEDIA_IDENTITY_CONFLICT");
          }
          continue;
        }
        depSeen.set(uuid, rec);
        await validatePersistedMediaAsset(rec, signal);
        depProjected.set(uuid, rec.byteLength);
        depTotal += rec.byteLength;
        if (
          depProjected.size > MAX_MEDIA_ASSETS ||
          depTotal > MAX_MEDIA_PACKAGE_BYTES
        ) {
          throw new Error("MEDIA_PACKAGE_TOO_LARGE");
        }
      }
    }
  }

  const hostBundles =
    Symbol.iterator in (bundles as any)
      ? (bundles as Iterable<{ map?: Record<string, any> }>)
      : [bundles as { map?: Record<string, any> }];

  const seen = new Map<string, PersistedMediaAssetRecord>();
  const bundleList = [...hostBundles];
  for (const bundle of bundleList) {
    for (const rec of persistedMediaRecords(bundle)) {
      stop();
      const uuid = persistedMediaAssetUuid(rec);
      const prior = seen.get(uuid);
      if (prior) {
        if (
          MEDIA_IDENTITY_FIELDS.some(
            (field) => !isEqual(prior[field], rec[field])
          )
        ) {
          throw new Error("MEDIA_IDENTITY_CONFLICT");
        }
        continue;
      }
      seen.set(uuid, rec);
      await validatePersistedMediaAsset(rec, signal);
    }
  }
  // Bounds apply to the FINAL native membership (array cardinality and bytes);
  // Site array replacement, deletedIids and fresh-uuid replacement all
  // supersede live assets. Chained bundles keep iids.
  let pending: InventoryEntry[] = (opts?.site?.mediaAssets ?? []).map((a) => ({
    uuid: a.uuid,
    byteLength: a.byteLength,
    iid: opts?.iidOf?.(a),
    record: a,
  }));
  const siteIid = opts?.site
    ? opts.iidOf?.(opts.site as unknown as MediaAsset)
    : undefined;
  for (const bundle of bundleList) {
    pending = projectPartialInventory(
      bundle,
      "mediaAssets",
      "MediaAsset",
      pending,
      opts?.deletedIids,
      {
        invalid: "MEDIA_MEMBERSHIP_INVALID",
        tooLarge: "MEDIA_PACKAGE_TOO_LARGE",
        max: MAX_MEDIA_ASSETS,
      },
      siteIid
    );
  }
  let total = 0;
  for (const e of pending) total += e.byteLength;
  if (pending.length > MAX_MEDIA_ASSETS || total > MAX_MEDIA_PACKAGE_BYTES) {
    throw new Error("MEDIA_PACKAGE_TOO_LARGE");
  }
}
